import React, { useState } from "react";
import { Stack, Check, X, CaretDown } from "@phosphor-icons/react";

/**
 * The working-out. Every problem between the buyer and the outcome, the solution
 * to each, and the delivery vehicles that were kept or cut on value-vs-cost.
 * This is where the offer came FROM — without it the result is just assertion.
 */
export default function OfferConstruction({ enumeration, naming }) {
  const [open, setOpen] = useState(false);
  if (!enumeration || !enumeration.problems || enumeration.problems.length === 0)
    return null;

  const slots = naming
    ? [
        ["Magnet", naming.magnet],
        ["Who", naming.avatar],
        ["Goal", naming.goal],
        ["Time", naming.interval],
        ["Vehicle", naming.container],
      ].filter(([, v]) => v)
    : [];

  return (
    <section className="mt-20" data-testid="construction-section">
      <div className="flex items-center gap-3 mb-2">
        <Stack weight="fill" className="h-5 w-5 text-[#C9A961]" />
        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
          How this offer was built
        </h2>
      </div>
      <p className="text-[#9AA5B5] max-w-3xl mb-8">
        {enumeration.problems.length} problems stand between your buyer and the
        result. {enumeration.kept_count} ways of solving them were worth keeping;{" "}
        {enumeration.cut_count} cost more to deliver than they were worth.
      </p>

      {naming && naming.name && (
        <div
          className="bg-[#0F1F38] border border-white/5 rounded-lg p-8 mb-6"
          data-testid="naming-card"
        >
          <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-3">
            The name, and where each piece of it came from
          </div>
          <div className="font-display text-3xl font-black text-white mb-5">
            {naming.name}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {slots.map(([k, v]) => (
              <div key={k}>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B]">
                  {k}
                </div>
                <div className="text-sm text-[#F5F1E8]">{v}</div>
              </div>
            ))}
          </div>
          {naming.alternatives && naming.alternatives.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/5 text-sm text-[#9AA5B5]">
              Also considered: {naming.alternatives.join(" · ")}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between bg-[#0F1F38] border border-white/5 rounded-lg px-6 py-4 text-left hover:border-[#C9A961]/40 transition-colors"
        data-testid="construction-toggle"
      >
        <span className="text-sm font-bold text-[#F5F1E8]">
          {open ? "Hide" : "Show"} the problem-by-problem working
        </span>
        <CaretDown
          weight="bold"
          className={`h-4 w-4 text-[#C9A961] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div
          className="mt-4 bg-[#0F1F38] border border-white/5 rounded-lg divide-y divide-white/5"
          data-testid="construction-list"
        >
          {enumeration.problems.map((p, i) => (
            <div key={i} className="p-6 md:p-8">
              <div className="text-xs tracking-[0.2em] uppercase text-[#808A9B] font-bold mb-2">
                Problem {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-display font-bold text-white text-lg mb-1">
                {p.problem}
              </div>
              {p.why_it_blocks && (
                <p className="text-sm text-[#9AA5B5] mb-3">{p.why_it_blocks}</p>
              )}
              <p className="text-[#F5F1E8] leading-relaxed mb-4">
                <span className="text-[#C9A961] font-bold">Solution: </span>
                {p.solution}
              </p>
              <div className="flex flex-wrap gap-2">
                {(p.delivery_vehicles || []).map((v, vi) => (
                  <div
                    key={vi}
                    className={`text-xs px-3 py-2 rounded-sm border flex items-center gap-2 ${
                      v.keep
                        ? "border-[#C9A961]/40 text-[#F5F1E8]"
                        : "border-white/10 text-[#5A6577] line-through"
                    }`}
                    title={
                      v.keep
                        ? `value ${v.value_to_customer}/10, cost ${v.cost_to_deliver}/10`
                        : v.reason || "cut"
                    }
                  >
                    {v.keep ? (
                      <Check weight="bold" className="h-3 w-3 text-[#C9A961]" />
                    ) : (
                      <X weight="bold" className="h-3 w-3" />
                    )}
                    {v.name}
                    <span className="opacity-60">
                      v{v.value_to_customer}/c{v.cost_to_deliver}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
